/**
 * Qué quiere decir cada término del producto, en castellano y en UN solo lugar.
 *
 * Existe porque estos números deciden compras y sus nombres son de manual de inventarios:
 * "stock de seguridad", "punto de reorden", "WAPE". Quien los mira es alguien de compras,
 * no quien programó el motor, y hasta ahora la única explicación estaba en la columna
 * "razón" de cada fila — que explica ESE producto, no el concepto.
 *
 * Un solo lugar a propósito: el mismo término aparece en la tabla del forecast, en la ficha
 * del producto y en el semáforo. Con una copia por pantalla, la tercera vez que se afine la
 * redacción dos van a decir cosas distintas.
 *
 * Reglas de redacción, que son las del proyecto:
 *  · nada de jerga sin traducir — si hay que nombrar la sigla, se nombra al final;
 *  · decir cómo se LEE el número, no sólo qué es;
 *  · y no prometer nada que el motor no haga (no hay estacionalidad, y se dice).
 */
export interface Termino {
  titulo: string;
  texto: string;
}

export const GLOSARIO = {
  metodo: {
    titulo: 'Método',
    texto:
      'Con qué cuenta se proyecta la demanda de este producto. Hoy es una sola para todos: el promedio de lo que vendió en los últimos 365 días. No es una simplificación — se eligió midiendo contra las series reales del ERP, donde erraba la mitad que el suavizamiento que había antes. Debajo dice en cuántos de esos días llegó a vender algo: mientras más salteado vende, menos preciso es cualquier pronóstico.',
  },
  demandaDia: {
    titulo: 'Demanda por día',
    texto:
      'Cuántas unidades se van vendiendo por día, en promedio del último año. El ± de abajo es cuánto suele apartarse de ese promedio: un número grande significa que vende a los saltos (algunos días mucho, muchos días nada), y eso es justamente lo que obliga a tener colchón.',
  },
  stock: {
    titulo: 'Stock',
    texto: 'Las unidades que hay hoy en bodega, según la última actualización de datos del B2B.',
  },
  stockSeguridad: {
    titulo: 'Stock de seguridad',
    texto:
      'El colchón: unidades de más que conviene tener para no quebrar cuando un mes se vende más que de costumbre o el proveedor se demora. Por defecto lo calcula el sistema con qué tan parejo vende el producto y el nivel de servicio de Configuración, con un tope para que uno que vende a los saltos no pida un colchón de años. Si preferís decidirlo vos, se le puede fijar un porcentaje a cada producto en Configuración por Producto.',
  },
  colchonPct: {
    titulo: '% de colchón',
    texto:
      'Cuánto colchón querés para ESTE producto, como porcentaje de lo que se vende mientras el proveedor fabrica. Con 20% y una espera de 133 días vendiendo 10 por día, el colchón es 266 unidades (un quinto de esa espera). En «auto» lo calcula el sistema solo, mirando qué tan despareja es la venta. Vaciá el campo para volver a automático — dejarlo en 0 no es lo mismo: 0 significa «sin colchón».',
  },
  puntoReorden: {
    titulo: 'Punto de reorden',
    texto:
      'El nivel de stock en el que hay que emitir la compra. No es un mínimo deseable: es el momento. Por defecto sale de lo que se vende durante la espera del proveedor más el colchón — si esperás a estar por debajo, la mercadería llega tarde por definición. Se le puede fijar un número a mano por producto, y en ese caso manda ese: el sistema te avisa si con la demanda de hoy la cuenta daría muy distinto.',
  },
  cuandoPedir: {
    titulo: 'Cuándo pedir',
    texto:
      'Cuántos días faltan para llegar al punto de reorden con la venta de hoy. «Ahora» quiere decir que ya está por debajo: cada día que pasa es riesgo de quiebre. Debajo va el tiempo que tarda el proveedor, que es lo que hace que haya que pedir con tanta anticipación.',
  },
  // ⚠️ `errorMedido` se quitó del glosario junto con el dato: no se muestra en ninguna
  // pantalla, y un término explicado que no aparece en ningún lado es código muerto — de esos
  // que este proyecto ya se comió una vez con el campo `ayuda` de METODO.
  cobertura: {
    titulo: 'Cobertura',
    texto:
      'Para cuántos días alcanza el stock de hoy si se sigue vendiendo al ritmo actual. En cero significa que ya no hay.',
  },
  tiempoProduccion: {
    titulo: 'Tiempo de producción',
    texto:
      'Cuánto tarda el proveedor desde que se le emite la orden hasta que la mercadería está disponible: fabricación, tráfico y nacionalización. Es el dato del que dependen la fecha y la cantidad de compra. Si dice «por defecto», es que ese producto todavía no lo tiene cargado y corre con un supuesto de 30 días.',
  },
  valorUnitario: {
    titulo: 'Valor unitario',
    texto:
      'Lo que cuesta una unidad. Sin este dato el sistema puede decir cuánto y cuándo comprar, pero no cuánta plata hace falta — por eso donde falta se dice «sin costo» en lugar de mostrar un cero, que se leería como que sale gratis.',
  },
  forma: {
    titulo: 'Cómo vende',
    texto:
      'De qué manera se comporta la venta del producto: todos los días, seguido, o muy salteado. No cambia la cuenta que se usa, pero sí cuánta confianza merece el resultado: sobre un producto que vende dos veces al año no hay pronóstico que valga.',
  },
} as const satisfies Record<string, Termino>;

export type ClaveGlosario = keyof typeof GLOSARIO;
